import { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { partnerApiService } from '@/services/partnerApiService'
import { 
  BarChart3, 
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  Calendar,
  Activity,
  PieChart,
  Filter
} from 'lucide-react'
import { fmtKrw, fmtUsdt } from '@/utils/currency'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

interface Partner {
  id: number
  name: string
  company?: string
  can_view_users: boolean
  can_view_trading: boolean
  can_view_deposits: boolean
  can_view_withdrawals: boolean
  can_view_fees: boolean
  can_view_statistics: boolean
}

interface Statistics {
  overview: {
    total_users: number
    active_users_today: number
    total_volume_usdt: number
    total_trades: number
    total_fees: number
    growth_rate: number
  }
  daily_stats: Array<{
    date: string
    new_users: number
    active_users: number
    volume_usdt: number
    trades: number
    fees: number
  }>
  user_distribution: Array<{
    status: string
    count: number
    percentage: number
  }>
  trading_pairs: Array<{
    symbol: string
    volume_usdt: number
    trades: number
    fees: number
    percentage: number
  }>
  monthly_summary: Array<{
    month: string
    users: number
    volume: number
    volume_usdt?: number
    trades: number
    fees: number
  }>
}

const PartnerStatistics = () => {
  const { partner } = useOutletContext<{ partner: Partner }>()
  const { t, i18n } = useTranslation('statistics')
  const locale = i18n.language === 'ko' ? 'ko-KR' : i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'zh' ? 'zh-CN' : 'en-US'
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('30d') // 7d, 30d, 90d

  // 권한 확인
  if (!partner.can_view_statistics) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">{t('common:noPermission')}</h2>
          <p className="text-dark-400">{t('noPermission')}</p>
        </div>
      </div>
    )
  }

  useEffect(() => {
    loadStatistics()
  }, [timeRange])

  const loadStatistics = async () => {
    try {
      setLoading(true)

      // Bearer 토큰 자동 첨부되는 표준 서비스 계층 사용 (2026-07-25 — raw fetch 쿠키 방식은 401 위험)
      const data = await partnerApiService.getStatistics(timeRange) as unknown as Statistics
      setStatistics(data)
    } catch (error) {
      console.error('Error loading statistics:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' USDT'
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat(locale).format(num)
  }

  const formatPercent = (percent: number) => {
    return percent.toFixed(2) + '%'
  }

  const getGrowthColor = (rate: number) => {
    if (rate > 0) return 'text-green-400'
    if (rate < 0) return 'text-red-400'
    return 'text-gray-400'
  }

  const getGrowthIcon = (rate: number) => {
    if (rate > 0) return <TrendingUp className="w-4 h-4" />
    if (rate < 0) return <TrendingDown className="w-4 h-4" />
    return null
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <LoadingSpinner />
      </div>
    )
  }

  if (!statistics) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">{t('noData')}</h2>
          <p className="text-dark-400">{t('noDataDescription')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* 페이지 헤더 */}
      <div className="border-b border-white/10 pb-4 sm:pb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">{t('title')}</h1>
            <p className="text-dark-400 text-sm sm:text-base">{t('subtitle')}</p>
          </div>

          {/* 기간 선택 */}
          <div className="relative flex-shrink-0">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-dark-400" />
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="w-full sm:w-auto pl-9 sm:pl-10 pr-8 py-2.5 sm:py-3 bg-dark-800 border border-white/10 rounded-lg text-white text-sm sm:text-base focus:border-primary-500 focus:outline-none sm:min-w-[150px]"
            >
              <option value="7d">{t('timeRange.7d')}</option>
              <option value="30d">{t('timeRange.30d')}</option>
              <option value="90d">{t('timeRange.90d')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* 전체 개요 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-dark-400 mb-1">{t('overview.totalUsers')}</p>
              <p className="text-xl sm:text-2xl font-bold text-white">{formatNumber(statistics.overview.total_users)}</p>
              <div className={`flex items-center text-xs mt-1 ${getGrowthColor(statistics.overview.growth_rate)}`}>
                {getGrowthIcon(statistics.overview.growth_rate)}
                <span className="ml-1">{formatPercent(Math.abs(statistics.overview.growth_rate))}</span>
              </div>
            </div>
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-dark-400 mb-1">{t('overview.activeUsersToday')}</p>
              <p className="text-xl sm:text-2xl font-bold text-white">{formatNumber(statistics.overview.active_users_today)}</p>
            </div>
            <div className="p-3 bg-green-500/20 rounded-lg">
              <Activity className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-dark-400 mb-1">{t('overview.totalVolume')} <span className="text-dark-500">{t('overview.legacyMarket')}</span></p>
              <p className="text-xl font-bold text-white">{formatCurrency(statistics.overview.total_volume_usdt)}</p>
              <p className="text-xs text-emerald-300 mt-1">{t('overview.nfContracts', { count: Number((statistics.overview as any).nf_contracts ?? 0) })}</p>
            </div>
            <div className="p-3 bg-purple-500/20 rounded-lg">
              <BarChart3 className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </div>

        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-dark-400 mb-1">{t('overview.totalTrades')}</p>
              <p className="text-xl sm:text-2xl font-bold text-white">{formatNumber(statistics.overview.total_trades)}</p>
            </div>
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <TrendingUp className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-dark-400 mb-1">{t('overview.totalFees')}</p>
              <p className="text-xl font-bold text-white">{formatCurrency(statistics.overview.total_fees)}</p>
              <p className="text-xs text-amber-300 mt-1">{t('overview.nfFees', { amount: fmtKrw((statistics.overview as any).nf_fees_krw) })}</p>
            </div>
            <div className="p-3 bg-red-500/20 rounded-lg">
              <DollarSign className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>

        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-dark-400 mb-1">{t('overview.growthRate')}</p>
              <p className={`text-xl sm:text-2xl font-bold ${getGrowthColor(statistics.overview.growth_rate)}`}>
                {statistics.overview.growth_rate > 0 ? '+' : ''}{formatPercent(statistics.overview.growth_rate)}
              </p>
            </div>
            <div className="p-3 bg-indigo-500/20 rounded-lg">
              {statistics.overview.growth_rate > 0 ? 
                <TrendingUp className="w-6 h-6 text-indigo-400" /> : 
                <TrendingDown className="w-6 h-6 text-indigo-400" />
              }
            </div>
          </div>
        </div>
      </div>

      {/* 일별 통계 차트 */}
      <div className="glass-card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          {t('dailyActivity')}
        </h3>
        
        {/* 데스크톱 테이블 */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('daily.date')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('daily.newUsers')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('daily.activeUsers')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('daily.volume')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('daily.trades')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('daily.fees')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {statistics.daily_stats.slice(0, 10).map((day) => (
                <tr key={day.date} className="hover:bg-white/5">
                  <td className="px-4 py-3 text-sm text-white">
                    {new Date(day.date).toLocaleDateString(locale)}
                  </td>
                  <td className="px-4 py-3 text-sm text-green-400">+{formatNumber(day.new_users)}</td>
                  <td className="px-4 py-3 text-sm text-white">{formatNumber(day.active_users)}</td>
                  <td className="px-4 py-3 text-sm text-white">{formatCurrency(day.volume_usdt)}</td>
                  <td className="px-4 py-3 text-sm text-white">{formatNumber(day.trades)}</td>
                  <td className="px-4 py-3 text-sm text-yellow-400">{formatCurrency(day.fees)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* 모바일 카드 목록 */}
        <div className="lg:hidden divide-y divide-white/10">
          {statistics.daily_stats.slice(0, 10).map((day) => (
            <div key={day.date} className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-white font-medium">
                  {new Date(day.date).toLocaleDateString(locale)}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-dark-400 mb-1">{t('daily.newUsers')}</div>
                  <div className="text-green-400 font-medium">+{formatNumber(day.new_users)}</div>
                </div>
                <div>
                  <div className="text-dark-400 mb-1">{t('daily.activeUsers')}</div>
                  <div className="text-white font-medium">{formatNumber(day.active_users)}</div>
                </div>
                <div>
                  <div className="text-dark-400 mb-1">{t('daily.volume')}</div>
                  <div className="text-white font-medium">{formatCurrency(day.volume_usdt)}</div>
                </div>
                <div>
                  <div className="text-dark-400 mb-1">{t('daily.trades')}</div>
                  <div className="text-white font-medium">{formatNumber(day.trades)}</div>
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="text-dark-400 text-xs mb-1">{t('daily.fees')}</div>
                <div className="text-yellow-400 font-medium">{formatCurrency(day.fees)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 사용자 분포 */}
        <div className="glass-card p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <PieChart className="w-5 h-5 mr-2" />
            {t('userDistribution')}
          </h3>
          <div className="space-y-4">
            {statistics.user_distribution.map((item, index) => (
              <div key={item.status} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded-full mr-3"
                    style={{ backgroundColor: `hsl(${index * 60}, 70%, 50%)` }}
                  ></div>
                  <span className="text-white">{t(`common:status.${item.status}`, { defaultValue: item.status })}</span>
                </div>
                <div className="text-right">
                  <div className="text-white font-medium">{formatNumber(item.count)}</div>
                  <div className="text-sm text-dark-400">{formatPercent(item.percentage)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 인기 거래 쌍 */}
        <div className="glass-card p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2" />
            {t('tradingPairs')}
          </h3>
          <div className="space-y-4">
            {statistics.trading_pairs.slice(0, 5).map((pair, index) => (
              <div key={pair.symbol} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                    {index + 1}
                  </div>
                  <div>
                    <div className="text-white font-medium">{pair.symbol}</div>
                    <div className="text-sm text-dark-400">{formatPercent(pair.percentage)} {t('proportion')}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-medium">{formatCurrency(pair.volume_usdt)}</div>
                  <div className="text-sm text-dark-400">{String(t('common:tradeCount', { count: formatNumber(pair.trades) } as any))}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 해외선물(NF) 인기 종목 — 원화 (2026-08-31) */}
        {((statistics as any).nf_pairs || []).length > 0 && (
          <div className="glass-card p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-emerald-400" />
              {t('nfPairs.title')}
            </h3>
            <div className="space-y-4">
              {((statistics as any).nf_pairs || []).slice(0, 5).map((pair: any, index: number) => (
                <div key={pair.symbol} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                      {index + 1}
                    </div>
                    <div>
                      <div className="text-white font-medium">{pair.symbol}</div>
                      <div className="text-sm text-dark-400">{t('nfPairs.contracts', { count: Number(pair.contracts || 0) })}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-amber-300 font-medium">{fmtKrw(pair.fees_krw)}</div>
                    <div className="text-sm text-dark-400">{String(t('common:tradeCount', { count: Number(pair.trades || 0).toLocaleString() } as any))}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 월별 요약 */}
      <div className="glass-card p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2" />
          {t('monthlySummary')}
        </h3>
        
        {/* 데스크톱 테이블 */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('monthly.month')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('monthly.users')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('monthly.volume')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('monthly.trades')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">{t('monthly.fees')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {statistics.monthly_summary.map((month) => (
                <tr key={month.month} className="hover:bg-white/5">
                  <td className="px-4 py-3 text-sm text-white font-medium">{month.month}</td>
                  <td className="px-4 py-3 text-sm text-white">{formatNumber(month.users)}</td>
                  <td className="px-4 py-3 text-sm text-white">{formatCurrency(month.volume)}</td>
                  <td className="px-4 py-3 text-sm text-white">{formatNumber(month.trades)}</td>
                  <td className="px-4 py-3 text-sm text-yellow-400">{formatCurrency(month.fees)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* 모바일 카드 목록 */}
        <div className="lg:hidden divide-y divide-white/10">
          {statistics.monthly_summary.map((month) => (
            <div key={month.month} className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-white font-medium">{month.month}</div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-dark-400 mb-1">{t('monthly.users')}</div>
                  <div className="text-white font-medium">{formatNumber(month.users)}</div>
                </div>
                <div>
                  <div className="text-dark-400 mb-1">{t('monthly.volume')}</div>
                  <div className="text-white font-medium">{formatCurrency(month.volume_usdt || month.volume)}</div>
                </div>
                <div>
                  <div className="text-dark-400 mb-1">{t('monthly.trades')}</div>
                  <div className="text-white font-medium">{formatNumber(month.trades)}</div>
                </div>
                <div>
                  <div className="text-dark-400 mb-1">{t('monthly.fees')}</div>
                  <div className="text-yellow-400 font-medium">{formatCurrency(month.fees)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default PartnerStatistics